import {
  alpha,
  Button,
  Drawer,
  Fab,
  List,
  ListItem,
  ListItemIcon,
  makeStyles,
  Paper,
  Slide,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthProvider, useTranslate } from "react-admin";
import {
  CurrentLocationDto,
  DashboardApiFactory,
  LocationApiFactory,
  LocationHistoryEntryDto,
  TrailerGpsLocationDto,
  VehicleIgnitionStatus,
} from "../../common/data/service-proxies";
import { useAxiosInstance } from "../../common/hooks/useAxiosInstance";
import { GoogleMapMarkerInfoWindowContent } from "../locations/common/GoogleMapMarkerInfoWindowContent";
import { useGetDistanceFromBottom } from "../locations/common/useGetDistanceFromBottom";
import { convertHeadingToRotationDegrees } from "../locations/mapUtils";
import CloseIcon from "@material-ui/icons/Close";
import MinimizeIcon from "@material-ui/icons/Minimize";
import MaximizeIcon from "@material-ui/icons/Maximize";

import { DashboardControls } from "./DashboardControls";
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { AppBarSimpleSearchField } from "../common/data-grid-inputs/AppBarSimpleSearchField";
import {
  dutyStatusAbbreviations,
  SelectedGroupChangedEventArgs,
} from "../locations/locations-current/MapSummary";
import {
  DashboardMapSummary,
  MapSummaryAnimationState,
} from "./DashboardMapSummary";
import { SelectedMarker } from ".";
import TrailerOutlineIcon from "../../common/icons/TrailerOutlineIcon";
import NotListedLocationOutlinedIcon from "@material-ui/icons/NotListedLocationOutlined";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import BeenhereIcon from "@material-ui/icons/Beenhere";
import { parseISO, formatDistance } from "date-fns";
import { DashboardTrailerInfoWindowContent } from "./DashboardTrailerInfoWindowContent";
import { CurrentLocationView } from "../locations/locations-current/CurrentLocationView";
import { AppbarCompaniesFilter } from "../common/data-grid-inputs/AppBarCompaniesFilter";

import {
  GoogleMapControls,
  IMarkerBase,
  LocationsFetchType,
} from "../locations/types";
import { ImperativeGoogleMap } from "../locations/ImperativeGoogleMap";
import { DashboardMapSettings } from "./DashboardMapSettings";
import { localStorageService } from "../../common/util/localStorageService";

interface Props {
  basePath?: string;
}

const ARROW = "M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z";
const SQUARE = "M1 1 H13 V13 H1 Z";
function isTruck(
  value: IMarkerBase
): value is IMarkerBase & {
  infoWindowContent: React.ReactElement<CurrentLocationDto>;
} {
  return Boolean(
    value.infoWindowContent &&
      "props" in value.infoWindowContent &&
      "ignitionStatus" in value.infoWindowContent.props &&
      value.infoWindowContent.props.ignitionStatus
  );
}

const usePaperStyles = makeStyles<Theme>((theme) => ({
  root: {
    backgroundColor: `${alpha(
      theme.palette.background.paper,
      0.85
    )} !important`,
  },
}));

const useTrailerListStyles = makeStyles<Theme>((x) => ({
  trailerStopped: {
    color: x.palette.error.main,
  },
  trailerInMotion: {
    color: x.palette.primary.light,
  },
}));

const useTrailerListItemIconStyles = makeStyles<Theme>((theme) => ({
  root: {
    minWidth: "40px",
  },
}));

const isFilteredOutByCompanyId = (
  selectedCompanies: Array<string>,
  selectedVehicle?: CurrentLocationDto
) => {
  return (
    !!selectedCompanies.length &&
    !!selectedVehicle?.companyId &&
    !selectedCompanies.some((x) => Number(x) === selectedVehicle.companyId)
  );
};

export const DashboardMap = (props: Props) => {
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showInfoWindow, setShowInfoWindow] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<
    SelectedGroupChangedEventArgs
  >({ dutyStatus: "all", onlineStatus: "all" });
  const [clusteringEnabled, setClusteringEnabled] = useState<boolean>(
    localStorageService.getDashboardMapSettings()?.clusteringEnabled ?? true
  );

  const DRIVING_STATES = useMemo(
    () => ({
      IgnitionOnIdle: {
        markerColor: theme.palette.warning.main,
        iconPath: SQUARE,
      },
      IgnitionOff: {
        markerColor: theme.palette.grey[900],
        iconPath: SQUARE,
      },
      IgnitionOnDriving: {
        markerColor: theme.palette.primary.main,
        iconPath: ARROW,
      },
    }),
    [theme]
  );

  const axios = useAxiosInstance();
  const authProvider = useAuthProvider();
  const [trailers, setTrailers] = useState<TrailerGpsLocationDto[]>([]);
  const [fetchType, setFetchType] = useState<LocationsFetchType>(
    LocationsFetchType.Trucks
  );
  const { handleMapBoundsChanged } = useGetDistanceFromBottom();
  const classes = usePaperStyles();
  const trailerClasses = useTrailerListStyles();
  const trailerListItemIconClasses = useTrailerListItemIconStyles();
  const [selectedVehicle, setSelectedVehicle] = useState<SelectedMarker>();
  const [prevSelectedVehicle, setPrevSelectedVehicle] = useState<
    SelectedMarker
  >();

  const [controlsVisible, setControlsVisible] = useState<boolean>(false);
  const [highlightedRoute, setHighlightedRoute] = useState<
    Array<{ lat: number; lng: number }>
  >();
  const [visitedRoute, setVisitedRoute] = useState<
    Array<{ lat: number; lng: number }>
  >();
  const [locations, setLocations] = useState<Map<number, CurrentLocationDto>>(
    new Map<number, CurrentLocationDto>()
  );
  const locationsRef = useRef<Map<number, CurrentLocationDto>>(
    new Map<number, CurrentLocationDto>()
  );
  const trailersRef = useRef<TrailerGpsLocationDto[]>([]);
  const [selectedTrailer, setSelectedTrailer] = useState<string>();
  const [showShareLinks, setShowShareLinks] = useState<boolean>(false);

  const [selectedCompanies, setSelectedCompanies] = useState<Array<string>>([]);
  const handleSelectedCompaniesChange = useCallback((e: string[]) => {
    setSelectedCompanies(e);
  }, []);

  const handleShareLinksClick = useCallback(() => {
    setShowShareLinks(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowShareLinks(false);
  }, []);

  const serializedTheme = JSON.stringify(theme);

  // HACK: theme reference changes when the menu is shown/hidden, even though if shoudn't have.
  // This is why we use the memoized theme value which depends on the serializedTheme,
  // which WILL update if the theme really changes. This way we avoid the unnecessary re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedTheme = useMemo(() => theme, [serializedTheme]);
  const filterMarkers = useCallback(
    (
      fetchType: LocationsFetchType,
      searchText: string,
      selectedId: number | undefined
    ) => {
      mapRef.current?.hideMarkers(
        (x) =>
          !x.title
            ?.toLocaleLowerCase()
            .includes(searchText.toLocaleLowerCase()) ||
          x.type !== fetchType ||
          (!!selectedId && x.id !== selectedId)
      );
    },
    []
  );

  useEffect(() => {
    setSelectedTrailer(undefined);
    setSelectedVehicle(undefined);
    setSearchText("");
    filterMarkers(fetchType, "", undefined);
  }, [fetchType, filterMarkers]);

  useEffect(() => {
    if (isPlaying) setShowInfoWindow(false);
  }, [isPlaying]);

  const getTrailerListItemIcon = useCallback(
    (trailer: TrailerGpsLocationDto) => {
      if (selectedTrailer && trailer.name === selectedTrailer) {
        return <BeenhereIcon />;
      }

      if (trailer.inMotion !== true && trailer.inMotion !== false) {
        return <NotListedLocationOutlinedIcon />;
      }

      return <TrailerOutlineIcon />;
    },
    [selectedTrailer]
  );

  const pendingIgnitionStatuses = useRef<
    { id?: number; ignitionStatus?: string | null }[]
  >([]);

  const handleLocationUpdated = useCallback(
    (loc: CurrentLocationDto) => {
      if (
        !mapRef.current ||
        !loc.id ||
        !loc.lat ||
        !loc.lng ||
        !loc.vehicleName
      ) {
        return;
      }
      mapRef.current.updateMarker(loc.id, (inputMarker) => {
        let { markerColor, iconPath } = inputMarker;

        if (
          isTruck(inputMarker) &&
          (loc.ignitionStatus || typeof loc.drivingSpeed === "number")
        ) {
          let drivingState;
          if (loc.drivingSpeed === 0 || typeof loc.drivingSpeed !== "number") {
            drivingState =
              DRIVING_STATES[
                (loc.ignitionStatus ||
                  inputMarker.infoWindowContent.props.ignitionStatus) ===
                "IgnitionOff"
                  ? "IgnitionOff"
                  : "IgnitionOnIdle"
              ];
          } else {
            drivingState = DRIVING_STATES.IgnitionOnDriving;
          }
          markerColor = drivingState.markerColor;
          iconPath = drivingState.iconPath;
        }

        const newContent = (
          <GoogleMapMarkerInfoWindowContent
            key={Date.now()}
            driverFullName={
              loc.driverFullName ??
              translate("resources.locations-current.labels.unknownDriver")
            }
            dutyStatus={loc.dutyStatus ?? "Unknown"}
            drivingSpeed={loc.drivingSpeed}
            drivingSpeedUnit={loc.drivingSpeedUnit}
            vehicleName={loc.vehicleName === null ? undefined : loc.vehicleName}
            eldConnected={loc.eldConnected}
            geolocation={loc.location}
            trailers={loc.attachedTrailer}
            translate={translate}
            hosDetails={loc.hosDetails}
            ignitionStatus={
              loc.ignitionStatus
                ? loc.ignitionStatus
                : isTruck(inputMarker)
                ? inputMarker.infoWindowContent.props.ignitionStatus
                : undefined
            }
            healthStatus={loc.healthStatus}
            safetyStatus={loc.safetyStatus}
            mobileAppType={loc.mobileAppType}
            mobileAppVersion={loc.mobileAppVersion}
            theme={memoizedTheme}
          />
        );

        inputMarker.infoWindowContent = newContent;

        return {
          id: loc.id,
          lat: loc.lat as number,
          lng: loc.lng as number,
          dutyStatus: loc.dutyStatus ?? "Unknown",
          bearing:
            iconPath === ARROW
              ? loc.bearing ??
                convertHeadingToRotationDegrees(loc.headingDirection)
              : 0,
          title: loc.vehicleName as string | undefined,
          type: LocationsFetchType.Trucks,
          markerColor: markerColor,
          iconPath: iconPath,
          infoWindowContent: newContent,
          onClick: () => {
            setSelectedVehicle({ isTruck: true, truckOrTrailer: loc });
            filterMarkers(LocationsFetchType.Trucks, "", loc.id);
          },
        };
      });
    },
    [translate, filterMarkers, memoizedTheme, DRIVING_STATES]
  );

  const updateVehicleIgnitionStatuses = useCallback(() => {
    if (!pendingIgnitionStatuses.current.length) {
      return;
    }

    const newMap = new Map<number, CurrentLocationDto>(locationsRef.current);
    while (pendingIgnitionStatuses.current.length) {
      const input = pendingIgnitionStatuses.current.shift();
      if (!input?.id) {
        continue;
      }

      const previousLoc = locationsRef.current.get(input.id);
      if (!previousLoc) {
        continue;
      }

      if (input.ignitionStatus === previousLoc.ignitionStatus) {
        continue;
      }
      const newLoc = {
        ...previousLoc,
        ignitionStatus:
          input.ignitionStatus !== "1"
            ? VehicleIgnitionStatus.IgnitionOff
            : previousLoc.drivingSpeed
            ? VehicleIgnitionStatus.IgnitionOnDriving
            : VehicleIgnitionStatus.IgnitionOnIdle,
      };
      newMap.set(input.id, newLoc);
      handleLocationUpdated(newLoc);
    }
  }, [handleLocationUpdated]);

  const handleIgnitionUpdated = useCallback(
    (input: Partial<CurrentLocationDto>) => {
      pendingIgnitionStatuses.current.push(input);
    },
    []
  );

  useEffect(() => {
    const interval = setInterval(updateVehicleIgnitionStatuses, 30000);
    return () => clearInterval(interval);
  }, [updateVehicleIgnitionStatuses]);

  const handleTrailerUpdated = useCallback((input: TrailerGpsLocationDto) => {
    const newArray = [
      ...trailersRef.current.filter((x) => x.id !== input.id),
      { ...input },
    ];
    trailersRef.current = newArray;
    setTrailers(newArray);
  }, []);

  const handleSummaryAnimationStateChange = useCallback(
    (value: MapSummaryAnimationState) => {
      switch (value) {
        case MapSummaryAnimationState.TrailersSelected:
          setFetchType(LocationsFetchType.Trailers);
          break;
        case MapSummaryAnimationState.TrucksSelected:
          setFetchType(LocationsFetchType.Trucks);
          break;
        default:
          return;
      }
    },
    []
  );

  useEffect(() => {
    let hubConnection: HubConnection | undefined;
    if (!controlsVisible) {
      authProvider.checkAuth(null).then(() => {
        const apiUrl =
          process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
          "https://localhost:44327";

        hubConnection = new HubConnectionBuilder()
          .withUrl(`${apiUrl}/signalr-hubs/dashboard-hub`, {
            accessTokenFactory: authProvider.getAccessToken,
          })
          .withAutomaticReconnect([0, 2, 10, 20, 30, 40, 50, 60, 90, 120])
          .configureLogging(LogLevel.Information)
          .build();

        hubConnection.on("updateIgnitionStatus", handleIgnitionUpdated);
        hubConnection.on("updateLocation", handleLocationUpdated);
        hubConnection.on("updateTrailer", handleTrailerUpdated);
        hubConnection.start();
      });
    }

    return () => {
      if (hubConnection) {
        hubConnection.off("updateIgnitionStatus");
        hubConnection.off("updateLocation");
        hubConnection.off("updateTrailer");
        hubConnection.stop();
      }
    };
  }, [
    authProvider,
    controlsVisible,
    handleLocationUpdated,
    handleTrailerUpdated,
    handleIgnitionUpdated,
  ]);

  useEffect(() => {
    if (selectedVehicle) {
      setControlsVisible(true);
    }
  }, [selectedVehicle]);

  useEffect(() => {
    if (!axios) {
      return;
    }

    setLoading(true);
    const loadingState = {
      trucks: true,
      trailers: true,
    };

    if (loadingState.trucks) {
      DashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppDashboardDashboardlocationsdataGet(selectedCompanies.map(Number))
        .then((result) => {
          const locationsToSet = (result.data ?? []).reduce(
            (previous, current) => {
              previous.set(current.id, current);
              return previous;
            },
            new Map<number, CurrentLocationDto>()
          );
          locationsRef.current = locationsToSet;
          setLocations(locationsToSet);
        })
        .catch((e) => {
          // TODO: Notification
          console.error(e);
        })
        .finally(() => {
          loadingState.trucks = false;
          if (!loadingState.trailers) {
            setLoading(false);
          }
        });
    }

    if (loadingState.trailers) {
      LocationApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppTrailerLocationsGet()
        .then((result) => {
          setTrailers(result.data);
        })
        .catch((e) => {
          // TODO: Notification
          console.error(e);
        })
        .finally(() => {
          loadingState.trailers = false;
          if (!loadingState.trucks) {
            setLoading(false);
          }
        });
    }
  }, [axios, selectedCompanies]);

  const mapRef = useRef<GoogleMapControls>(null);

  const handleClusteringChange = useCallback(
    (enabled: boolean) => {
      const settings = localStorageService.getDashboardMapSettings() ?? {};
      localStorageService.setDashboardMapSettings({
        ...settings,
        clusteringEnabled: enabled,
      });
      setClusteringEnabled(enabled);
      if (enabled) {
        mapRef.current?.enableClustering();
      } else {
        mapRef.current?.disableClustering();
      }

      setSelectedTrailer(undefined);
      setSelectedVehicle(undefined);
      setSearchText("");
      filterMarkers(fetchType, "", undefined);
    },
    [mapRef, fetchType, filterMarkers]
  );

  const shouldHideTruckMarker = useCallback(
    (
      location: CurrentLocationDto,
      {
        ignoreOnline = false,
        ignoreDuty = false,
        ignoreAllFilters = false,
      }: {
        ignoreOnline?: boolean;
        ignoreDuty?: boolean;
        ignoreAllFilters?: boolean;
      }
    ) => {
      if (
        (fetchType & LocationsFetchType.Trucks) !== LocationsFetchType.Trucks &&
        !ignoreAllFilters
      ) {
        return true;
      }

      let isInGroup = true;
      if (!ignoreDuty) {
        switch (selectedGroup.dutyStatus) {
          case "all":
            break;
          default:
            isInGroup =
              !!location.dutyStatus &&
              dutyStatusAbbreviations[selectedGroup.dutyStatus].includes(
                location.dutyStatus
              );
            break;
        }
      }

      if (!ignoreOnline) {
        switch (selectedGroup.onlineStatus) {
          case "all":
            break;
          case "online":
            isInGroup = isInGroup && !!location.eldConnected;
            break;
          case "offline":
            isInGroup = isInGroup && !location.eldConnected;
        }
      }

      return (
        !isInGroup ||
        (searchText.length > 0 &&
          !!location.vehicleName &&
          !location.vehicleName.includes(searchText))
      );
    },
    [searchText, selectedGroup, fetchType]
  );

  const handleMapLoaded = useCallback(
    (loaded) => {
      console.debug("Handle map loaded", loaded);
      if (!loaded) {
        return;
      }

      if (!mapRef.current) {
        throw Error("The map appears to be loaded, but mapRef.current is null");
      }

      if (fetchType === LocationsFetchType.Trucks) {
        const truckMarkers = Array.from(locations.values())
          .map((loc) => {
            if (!loc.lat || !loc.lng || !loc.vehicleName) {
              return null;
            }

            const retVal: IMarkerBase = {
              id: loc.id,
              type: LocationsFetchType.Trucks,
              lat: loc.lat,
              lng: loc.lng,
              bearing:
                loc.ignitionStatus === "IgnitionOnDriving"
                  ? loc.bearing ??
                    convertHeadingToRotationDegrees(loc.headingDirection)
                  : 0,
              title: loc.vehicleName,
              dutyStatus: loc.dutyStatus ?? "Unknown",
              iconPath:
                loc.ignitionStatus === "IgnitionOnDriving" ? ARROW : SQUARE,
              markerColor:
                DRIVING_STATES[loc.ignitionStatus || "IgnitionOff"].markerColor,
              infoWindowContent: (
                <GoogleMapMarkerInfoWindowContent
                  driverFullName={
                    loc.driverFullName ??
                    translate(
                      "resources.locations-current.labels.unknownDriver"
                    )
                  }
                  dutyStatus={loc.dutyStatus ?? "Unknown"}
                  drivingSpeed={loc.drivingSpeed}
                  drivingSpeedUnit={loc.drivingSpeedUnit}
                  vehicleName={loc.vehicleName}
                  eldConnected={loc.eldConnected}
                  geolocation={loc.location}
                  trailers={loc.attachedTrailer}
                  translate={translate}
                  hosDetails={loc.hosDetails}
                  ignitionStatus={loc.ignitionStatus}
                  healthStatus={loc.healthStatus}
                  safetyStatus={loc.safetyStatus}
                  mobileAppType={loc.mobileAppType}
                  mobileAppVersion={loc.mobileAppVersion}
                  theme={memoizedTheme}
                />
              ),
              onClick: () => {
                setSelectedVehicle({ isTruck: true, truckOrTrailer: loc });
                setPrevSelectedVehicle({ isTruck: true, truckOrTrailer: loc });
                filterMarkers(LocationsFetchType.Trucks, "", loc.id);
              },
            };

            return retVal;
          })
          .filter((x) => !!x) as Array<IMarkerBase>;
        mapRef.current.initMarkers(truckMarkers);
        mapRef.current.resetView();
        return;
      }

      if (fetchType === LocationsFetchType.Trailers) {
        const trailerMarkers = trailers
          .map((loc) => {
            if (!loc.lat || !loc.lng || !loc.name) {
              return null;
            }

            const retVal: IMarkerBase = {
              id: loc.name,
              type: LocationsFetchType.Trailers,
              dutyStatus: "n/a",
              infoWindowContent: (
                <DashboardTrailerInfoWindowContent
                  name={loc.name}
                  translate={translate}
                  inMotion={loc.inMotion}
                  odometer={loc.odometer}
                  geolocation={loc.geolocation}
                  lastMotionStatusChange={loc.lastMotionStatusChange}
                  theme={memoizedTheme}
                />
              ),
              lat: loc.lat,
              lng: loc.lng,
              bearing: 0,
              title: loc.name,
              iconPath:
                "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
              markerColor: !!loc.id
                ? loc.inMotion
                  ? memoizedTheme.palette.primary.light
                  : memoizedTheme.palette.error.main
                : memoizedTheme.palette.grey[400],
              onClick: () => {
                setSelectedVehicle({ isTruck: false, truckOrTrailer: loc });
                filterMarkers(
                  LocationsFetchType.Trailers,
                  "",
                  loc.id ?? undefined
                );
                mapRef.current?.resetView();
              },
            };

            return retVal;
          })
          .filter((x) => !!x) as Array<IMarkerBase>;
        mapRef.current.initMarkers(trailerMarkers);
        mapRef.current.resetView();
        return;
      }
    },
    [
      fetchType,
      locations,
      trailers,
      translate,
      filterMarkers,
      memoizedTheme,
      DRIVING_STATES,
    ]
  );

  const count = useMemo(() => {
    const dataItems = Array.from(locations.values()).filter(
      (x) => !isFilteredOutByCompanyId(selectedCompanies, x)
    );
    return {
      all: dataItems.length,
      disconnected: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: true,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) && !x.eldConnected
      ).length,
      connected: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: true,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) && !!x.eldConnected
      ).length,
      onDuty: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: false,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) &&
          (x.dutyStatus === "ON" || x.dutyStatus === "YM")
      ).length,
      offDuty: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: false,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) &&
          (x.dutyStatus === "OFF" || x.dutyStatus === "PC")
      ).length,
      driving: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: false,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) && x.dutyStatus === "D"
      ).length,
      sleeper: dataItems.filter(
        (x) =>
          !shouldHideTruckMarker(x, {
            ignoreOnline: false,
            ignoreDuty: true,
            ignoreAllFilters: true,
          }) && x.dutyStatus === "SB"
      ).length,
      trailers: trailers.length,
    };
  }, [locations, shouldHideTruckMarker, trailers, selectedCompanies]);

  const handleSearchTextChange = useCallback(
    (text: string) => {
      setSearchText(text);
      filterMarkers(fetchType, text, undefined);
    },
    [fetchType, filterMarkers]
  );

  const showSidebar =
    fetchType === LocationsFetchType.Trailers && !selectedVehicle;
  const handleTrailerClick = (name: string) => {
    if (!name) {
      setSelectedTrailer(undefined);
      mapRef.current?.hideMarkers((x) => x.type !== fetchType);
      return;
    }

    if (searchText === name) {
      setSearchText("");
      setSelectedTrailer(undefined);
      mapRef.current?.hideMarkers((x) => x.type !== fetchType);

      return;
    }

    setSearchText(name);
    setSelectedTrailer(name);
    filterMarkers(LocationsFetchType.Trailers, name, undefined);
  };

  const onCurrentLocationChange = (location: LocationHistoryEntryDto) => {
    if (controlsVisible) {
      const tempVehicle = {
        ...(selectedVehicle?.truckOrTrailer as CurrentLocationDto),
      };
      tempVehicle.lat = location.lat;
      tempVehicle.lng = location.lng;
      tempVehicle.drivingSpeed = location.drivingSpeed;
      tempVehicle.driverFullName = location.driverDisplayName;
      tempVehicle.location = location.geolocation;
      tempVehicle.bearing = location.bearing;
      tempVehicle.headingDirection = location.headingDirection;
      tempVehicle.attachedTrailer = location.attachedTrailers;

      handleLocationUpdated(tempVehicle);
    } else {
      return;
    }
  };

  return (
    <>
      <AppbarCompaniesFilter
        selectedCompanies={selectedCompanies}
        onSelectedCompaniesChange={handleSelectedCompaniesChange}
      />

      <AppBarSimpleSearchField
        onSearchTextChange={handleSearchTextChange}
        searchText={searchText}
      />

      <div className="dashboard-map-wrapper">
        <DashboardMapSummary
          loading={loading}
          onAnimationStateChange={handleSummaryAnimationStateChange}
          initialGroups={selectedGroup}
          onGroupChanged={(group) => {
            setSelectedGroup(group);
            setSearchText("");
            mapRef.current?.hideMarkers((x) =>
              group.dutyStatus === "all"
                ? false
                : !group.dutyStatus.includes(x.dutyStatus)
            );
          }}
          theme={theme}
          translate={translate}
          {...count}
        />
        <DashboardMapSettings
          clusteringEnabled={clusteringEnabled}
          onSetClusteringEnabled={handleClusteringChange}
          loading={loading}
        />
        <ImperativeGoogleMap
          clusteringEnabled={clusteringEnabled}
          onMapLoaded={handleMapLoaded}
          loading={loading}
          zoomControl={false}
          mapTypeControl={true}
          fullscreenControl={false}
          onBoundsChanged={handleMapBoundsChanged}
          highlightedRoute={highlightedRoute}
          visitedRoute={visitedRoute}
          ref={mapRef}
        />
        <Slide
          direction="up"
          in={controlsVisible}
          mountOnEnter
          onExited={() => {
            setSelectedTrailer(undefined);
            setSelectedVehicle(undefined);
            setHighlightedRoute(undefined);
            setVisitedRoute(undefined);
            setSearchText("");
            mapRef.current?.resetView();
            filterMarkers(fetchType, "", undefined);
          }}
        >
          <Paper classes={{ root: classes.root }} className="vehicle-controls">
            <Fab
              color="primary"
              size="small"
              className="close-button"
              onClick={() => {
                setIsPlaying(false);
                setControlsVisible(false);
                const tempVehicle = {
                  ...(prevSelectedVehicle?.truckOrTrailer as CurrentLocationDto),
                };
                handleLocationUpdated(tempVehicle);
              }}
            >
              <CloseIcon />
            </Fab>
            <Fab
              color="primary"
              size="small"
              className="minimize-button"
              onClick={() => {
                setShowInfoWindow(!showInfoWindow);
              }}
            >
              {showInfoWindow ? <MinimizeIcon /> : <MaximizeIcon />}
            </Fab>
            {selectedVehicle ? (
              <>
                <DashboardControls
                  selectedVehicle={selectedVehicle}
                  onSetHighlightedRoute={setHighlightedRoute}
                  onSetVisitedRoute={setVisitedRoute}
                  onShareLinksClick={handleShareLinksClick}
                  onSetIsPlaying={setIsPlaying}
                  onCurrentLocationChange={onCurrentLocationChange}
                  mapRef={mapRef}
                />
                {selectedVehicle.isTruck && showInfoWindow ? (
                  <>
                    <GoogleMapMarkerInfoWindowContent
                      driverFullName={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .driverFullName ??
                        translate(
                          "resources.locations-current.labels.unknownDriver"
                        )
                      }
                      dutyStatus={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .dutyStatus ?? "Unknown"
                      }
                      drivingSpeed={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .drivingSpeed
                      }
                      drivingSpeedUnit={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .drivingSpeedUnit
                      }
                      vehicleName={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .vehicleName!
                      }
                      eldConnected={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .eldConnected
                      }
                      geolocation={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .location
                      }
                      trailers={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .attachedTrailer
                      }
                      translate={translate}
                      hosDetails={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .hosDetails
                      }
                      ignitionStatus={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .ignitionStatus
                      }
                      healthStatus={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .healthStatus
                      }
                      safetyStatus={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .safetyStatus
                      }
                      mobileAppType={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .mobileAppType
                      }
                      mobileAppVersion={
                        (selectedVehicle.truckOrTrailer as CurrentLocationDto)
                          .mobileAppVersion
                      }
                      isMobile={true}
                      theme={memoizedTheme}
                    />
                  </>
                ) : null}

                {!selectedVehicle.isTruck && showInfoWindow ? (
                  <>
                    <DashboardTrailerInfoWindowContent
                      name={
                        (selectedVehicle.truckOrTrailer as TrailerGpsLocationDto)
                          .name!
                      }
                      translate={translate}
                      inMotion={
                        (selectedVehicle.truckOrTrailer as TrailerGpsLocationDto)
                          .inMotion
                      }
                      odometer={
                        (selectedVehicle.truckOrTrailer as TrailerGpsLocationDto)
                          .odometer
                      }
                      geolocation={
                        (selectedVehicle.truckOrTrailer as TrailerGpsLocationDto)
                          .geolocation
                      }
                      lastMotionStatusChange={
                        (selectedVehicle.truckOrTrailer as TrailerGpsLocationDto)
                          .lastMotionStatusChange
                      }
                      isMobile={true}
                      theme={memoizedTheme}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </Paper>
        </Slide>
        <Slide direction="left" in={showSidebar} mountOnEnter unmountOnExit>
          <Paper classes={{ root: classes.root }} className="trailers-list">
            <List>
              {trailers.map((x) =>
                x.id && !!x.name ? (
                  <ListItem
                    component={Button}
                    className="trailers-list-item"
                    key={x.name}
                    divider
                    onClick={() => handleTrailerClick(x.name ?? "")}
                  >
                    <ListItemIcon
                      classes={{ root: trailerListItemIconClasses.root }}
                      className={
                        x.inMotion !== true
                          ? trailerClasses.trailerStopped
                          : trailerClasses.trailerInMotion
                      }
                    >
                      {getTrailerListItemIcon(x)}
                    </ListItemIcon>
                    <div className="trailers-list-item-wrapper">
                      <div className="trailers-list-item-text">
                        <Typography variant="body2">{x.name}</Typography>
                        <Typography variant="caption">
                          {x.lastMotionStatusChange ? (
                            <div className="trailers-list-item-clock">
                              <AccessTimeIcon fontSize="small" />
                              <div>
                                for{" "}
                                {formatDistance(
                                  parseISO(x.lastMotionStatusChange),
                                  new Date()
                                )}
                              </div>
                            </div>
                          ) : null}
                        </Typography>
                      </div>
                    </div>
                  </ListItem>
                ) : null
              )}
            </List>
          </Paper>
        </Slide>
        <Drawer open={!!showShareLinks} anchor="right" onClose={handleClose}>
          {selectedVehicle?.truckOrTrailer.id ? (
            <CurrentLocationView
              id={selectedVehicle.truckOrTrailer.id}
              getData={(id) =>
                !!locations
                  ? (locations.get(id) as CurrentLocationDto)
                  : undefined
              }
              onCancel={handleClose}
              {...props}
            />
          ) : null}
        </Drawer>
      </div>
    </>
  );
};
